import ApiService from '@/services/apiService';
import { mapState, mapGetters } from 'vuex';
import { userItemsPerListMixin } from '@/utils/vuex';
import { localizeDate } from '@/utils/date';
import isEqual from 'lodash/isEqual';
import debounce from 'lodash/debounce';
import get from 'lodash/get';
const defaultItemsPerPage = 10;
const defaultTableOptions = {
  sortBy: ['createdAt'],
  sortDesc: [true],
  page: 1,
  itemsPerPage: defaultItemsPerPage
};
export default {
  name: 'CoreBackgroundJobsTable',
  mixins: [userItemsPerListMixin],
  props: {
    accounts: {
      type: Array,
      default: () => [],
      required: false
    },
    containerClass: {
      type: String,
      default: () => 'n20-mainrow',
      required: false
    },
    jobType: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      deletePrompt: false,
      cancelPromptCsvImport: false,
      cancelPromptBulkEdit: false,
      disableFetchOnCreatedMixin: true,
      lastSearch: null,
      searchItems: [],
      isFetching: false,
      tableOptions: {
        ...defaultTableOptions
      },
      selected: [],
      list: [],
      serverItemsLength: null,
      selectedItem: {}
    };
  },
  computed: {
    ...mapState('app', ['color', 'permissions']),
    ...mapGetters({
      sessionUserId: 'authentication/userId',
      isFeatureFlagEnabled: 'app/isFeatureFlagEnabled'
    }),
    isEmptyList() {
      return this.searchItems.length === 0 && this.serverItemsLength <= 0 && this.isFetching === false;
    },
    tableHeaders() {
      return [{
        text: this.$i18n_t('BackgroundJobs.BackgroundJobs.table.id'),
        value: 'id',
        align: 'center',
        sortable: false
      }, {
        text: this.$i18n_t('BackgroundJobs.BackgroundJobs.table.title'),
        value: 'title',
        align: 'start',
        sortable: false
      }, ...(this.showsJobType ? [{
        text: this.$i18n_t('BackgroundJobs.BackgroundJobs.jobType'),
        value: 'type',
        align: 'start',
        sortable: false
      }] : []), {
        text: this.$i18n_t('BackgroundJobs.BackgroundJobs.table.devicesNumber'),
        value: 'tasks',
        align: 'start',
        sortable: false
      }, {
        text: this.$i18n_t('BackgroundJobs.BackgroundJobs.table.state'),
        value: 'state',
        align: 'start',
        sortable: false
      }, {
        text: this.$i18n_t('BackgroundJobs.BackgroundJobs.table.errors'),
        value: 'tasks.errors',
        align: 'start',
        sortable: false
      }, {
        text: this.$i18n_t('BackgroundJobs.BackgroundJobs.table.createdAt'),
        value: 'createdAt'
      }, {
        text: this.$i18n_t('BackgroundJobs.BackgroundJobs.table.user'),
        value: 'userCreated',
        align: 'start',
        sortable: false
      }, ...(this.hasReadPermissions ? [{
        align: 'end',
        sortable: false,
        value: 'actions'
      }] : [])];
    },
    tableFooterProps() {
      return {
        itemsPerPageText: this.$i18n_t('Common.rowsPerPage'),
        itemsPerPageOptions: [defaultItemsPerPage, 20, 50]
      };
    },
    accountsIds() {
      return this.accounts.map(acc => acc.id);
    },
    // The bulk import list also lists IoT Data Hub migrations, but only once the feature is switched on:
    // until then it asks for csv imports alone and looks as it always did.
    listsMigrations() {
      return this.jobType === 'virtualDevicesCsvImport' && this.isFeatureFlagEnabled('env_iot_hub_migration_enabled');
    },
    requestedJobTypes() {
      return this.listsMigrations ? [this.jobType, 'iotHubMigration'] : [this.jobType];
    },
    // a list that mixes csv imports and migrations tells them apart in a type column
    showsJobType() {
      return this.listsMigrations;
    },
    hasReadPermissions() {
      switch (this.jobType) {
        case 'virtualDevicesEdit':
        case 'virtualDevicesCsvImport':
          return this.permissions['VirtualDevice.read'];
        case 'digitalTwinBulkDuplicate':
          return this.permissions['DigitalTwin.read'];
      }
      return this.permissions['VirtualDevice.read'] || this.permissions['DigitalTwin.read'];
    },
    canDeleteItem() {
      if (this.permissions['all.manage']) {
        return true;
      }
      switch (this.jobType) {
        case 'virtualDevicesEdit':
        case 'virtualDevicesCsvImport':
          return this.permissions['VirtualDevice.manage'];
        case 'digitalTwinBulkDuplicate':
          return this.permissions['DigitalTwin.manage'];
      }
      return this.permissions['VirtualDevice.manage'] || this.permissions['DigitalTwin.manage'];
    },
    noJobs() {
      switch (this.jobType) {
        case 'virtualDevicesEdit':
          return this.$i18n_t('BackgroundJobs.BackgroundJobs.emptyState.title');
        case 'virtualDevicesCsvImport':
          return this.$i18n_t('BackgroundJobs.BackgroundJobs.noBulkImportJobs');
        case 'digitalTwinBulkDuplicate':
          return this.$i18n_t('BackgroundJobs.BackgroundJobs.noBulkDuplicationJobs');
      }
      return this.$i18n_t('BackgroundJobs.BackgroundJobs.emptyState.title');
    },
    emptyStateIcon() {
      switch (this.jobType) {
        case 'digitalTwinBulkDuplicate':
          return 'fal fa-dice-d8';
        default:
          return 'fa-sensor-on';
      }
    }
  },
  watch: {
    accounts: debounce(function () {
      // Items have already been requested
      if (this.isFetching) {
        return;
      }
      this.fetchList();
    }, 500),
    canFetch() {
      if (!this.canFetch) {
        return;
      }
      this.fetchList();
    },
    'tableOptions.page'(newVal, oldVal) {
      if (newVal !== oldVal && this.canFetch) {
        this.fetchList();
      }
    }
  },
  async mounted() {
    await this.itemsPerPageReadyPromise;
    this.fetchList(true);
  },
  methods: {
    fetchList(fromStart = false) {
      if (this.isFetching) {
        return;
      }
      this.isFetching = true;
      const searchItems = (this.searchItems || []).map(item => ({
        variable: item.param,
        // support item.search.value === null, so do not simply have a >search.value || search< expression
        search: (item.search || {}).value === undefined ? item.search : item.search.value,
        key: item.key
      }));
      if (!isEqual(this.lastSearch, JSON.stringify(searchItems))) {
        this.lastSearch = JSON.stringify(searchItems);
        this.tableOptions = {
          ...this.tableOptions,
          page: 1
        };
      }
      const tableOptions = fromStart ? {
        ...this.tableOptions,
        page: 1
      } : this.tableOptions;
      let {
        sortBy,
        sortDesc,
        page,
        itemsPerPage
      } = tableOptions;
      sortBy = sortBy && sortBy.length ? sortBy[0] : 'createdAt';
      const doSortDesc = sortDesc && sortDesc.length ? sortDesc[0] : true;
      const usedPage = Math.max(1, page || 1);
      const limit = itemsPerPage || defaultItemsPerPage;
      const offset = (usedPage - 1) * limit;
      ApiService.fetchBackgroundJobByAccountIds(this.accountsIds, sortBy, doSortDesc, limit, offset, this.requestedJobTypes.join(',')).then(result => {
        this.list = result.data;
        if (searchItems.length) {
          this.tableOptions.itemsPerPage = 10;
        }
        this.serverItemsLength = result.pagination.itemCount;
        this.$emit('load-items', result);
      }).catch(error => {
        console.error('Error fetching virtual devices', error);
      }).finally(() => {
        this.isFetching = false;
      });
    },
    deletePromise() {
      return ApiService.deleteBackgroundRunner(this.selectedItem.id);
    },
    handleClickRunner(item) {
      this.$emit('show-item-info', item);
    },
    // a migration's tasks include the task that loads the devices, so its device count is kept separately
    deviceCount(item) {
      if (item.type !== 'iotHubMigration') {
        return item.tasks.total;
      }
      const deviceCount = get(item, 'executions.deviceCount');
      return typeof deviceCount === 'number' ? deviceCount : Math.max(0, item.tasks.total - 1);
    },
    localizeDate,
    showDeletePrompt(item) {
      this.selectedItem = item;
      this.deletePrompt = true;
    },
    showCancelPrompt(item) {
      this.selectedItem = item;
      // a migration leaves the virtual devices it already created in place, like a csv import
      item.type === 'virtualDevicesCsvImport' || item.type === 'digitalTwinBulkDuplicate' || item.type === 'iotHubMigration' ? this.cancelPromptCsvImport = true : item.type === 'virtualDevicesEdit' ? this.cancelPromptBulkEdit = true : null;
    },
    cancelRunner() {
      this.cancelPromptCsvImport = false;
      this.cancelPromptBulkEdit = false;
      ApiService.cancelBackgroundRunner(this.selectedItem.id).then(() => {
        this.$snack(this.$i18n_t('BackgroundJobs.BackgroundJobs.snacks.cancelSuccess'), 'success');
        this.fetchList();
      }).catch(error => {
        if (error.response && this.$i18n_t(`Error.${error.response.data.message}`)) {
          this.$snack(this.$i18n_t(`Error.${error.response.data.message}`), 'error');
        } else {
          this.$snack(this.$i18n_t('BackgroundJobs.BackgroundJobs.snacks.cancelFailed'), 'error');
        }
      });
    },
    deleteItem() {
      this.deletePrompt = false;
      this.deletePromise().then(() => {
        this.$snack(this.$i18n_t('BackgroundJobs.BackgroundJobs.snacks.deleteSuccess'), 'success');
        this.fetchList();
      }).catch(error => {
        console.error(this.$i18n_t('BackgroundJobs.BackgroundJobs.snacks.deleteFailed'), error);
        if (error.response && this.$i18n_t(`Error.${error.response.data.message}`)) {
          this.$snack(this.$i18n_t(`Error.${error.response.data.message}`), 'error');
        } else {
          this.$snack(this.$i18n_t('BackgroundJobs.BackgroundJobs.snacks.deleteFailed'), 'error');
        }
      });
    }
  }
};